/**
 * Bookings Redux Slice
 * 
 * Estado y acciones para la gestión de bookings
 * Listo para migrar la vista de bookings
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
// Tipos (importar desde booking-types cuando se migre)
export interface Booking {
  id: number;
  booking_reference: string | null;
  entity_id: number;
  carrier_id: number | null;
  booking_status_id: number | null;
  contract_header_id: number | null;
  booking_date: string | null;
  etd: string | null;
  eta: string | null;
  origin_port_id: number | null;
  destination_port_id: number | null;
  operation_type: string | null;
  freight_type: string | null;
  mbl: string | null;
  bl: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
interface BookingsState extends FilteredState<Booking> {
  selectedBooking: Booking | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
}
const initialState: BookingsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'id',
  sortDirection: 'desc',
  searchTerm: null,
  selectedBooking: null,
  creating: false,
  updating: false,
  deleting: false,
};
// Tipos de respuesta
interface BookingsResponse {
  data: Booking[];
  totalCount: number;
}
// Async Thunks (implementar cuando se migre)
export const fetchBookings = createAsyncThunk<BookingsResponse, { pagination?: PaginationParams; sort?: SortParams; filters?: FilterParams }>(
  'bookings/fetchBookings',
  async (params) => {
    // TODO: Implementar llamada a API
    throw new Error('Not implemented yet');
  }
);
export const fetchBookingById = createAsyncThunk<Booking, number>(
  'bookings/fetchBookingById',
  async (id) => {
    // TODO: Implementar llamada a API
    throw new Error('Not implemented yet');
  }
);
export const createBooking = createAsyncThunk<Booking, Partial<Booking>>(
  'bookings/createBooking',
  async (booking) => {
    // TODO: Implementar llamada a API
    throw new Error('Not implemented yet');
  }
);
export const updateBooking = createAsyncThunk<{ id: number; data: Booking }, { id: number; data: Partial<Booking> }>(
  'bookings/updateBooking',
  async ({ id, data }) => {
    // TODO: Implementar llamada a API
    return { id, data: { ...data, id } as Booking };
  }
);
export const deleteBooking = createAsyncThunk<number, number>(
  'bookings/deleteBooking',
  async (id) => {
    // TODO: Implementar llamada a API
    return id;
  }
);
const bookingsSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset a primera página al cambiar filtros
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectBooking: (state, action: PayloadAction<Booking | null>) => {
      state.selectedBooking = action.payload;
    },
    clearSelection: (state) => {
      state.selectedBooking = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch bookings
    builder
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar bookings';
      });
    // Fetch booking by ID
    builder
      .addCase(fetchBookingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBooking = action.payload;
        state.lastFetch = Date.now();
      })
      .addCase(fetchBookingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar booking';
      });
    // Create booking
    builder
      .addCase(createBooking.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.creating = false;
        state.items.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.creating = false;
        state.error = action.error.message || 'Error al crear booking';
      });
    // Update booking
    builder
      .addCase(updateBooking.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateBooking.fulfilled, (state, action) => {
        state.updating = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload.data;
        }
        if (state.selectedBooking?.id === action.payload.id) {
          state.selectedBooking = action.payload.data;
        }
      })
      .addCase(updateBooking.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || 'Error al actualizar booking';
      });
    // Delete booking
    builder
      .addCase(deleteBooking.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteBooking.fulfilled, (state, action) => {
        state.deleting = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedBooking?.id === action.payload) {
          state.selectedBooking = null;
        }
      })
      .addCase(deleteBooking.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.error.message || 'Error al eliminar booking';
      });
  },
});
export const {
  setFilters,
  clearFilters,
  setSearchTerm,
  setSort,
  setPage,
  setPageSize,
  selectBooking,
  clearSelection,
} = bookingsSlice.actions;
export default bookingsSlice.reducer;
